import {
  type BadgeTone,
  badgeToneClass,
  badgeToneDescription,
} from "components/ui/StatusBadge";
import { useMemo } from "react";
import type { BadgeAuditScreen } from "routes/SnippetsPage/badgeAudit";
import {
  badgeAuditProposal,
  type ProposalScreen,
} from "routes/SnippetsPage/badgeAuditProposal";

const TONE_ORDER: BadgeTone[] = [
  "soft-success",
  "soft-warning",
  "soft-error",
  "soft-info",
  "soft-neutral",
  "critical-error",
  "critical-success",
  "stage-1",
  "stage-2",
  "stage-3",
  "stage-4",
  "stage-final",
  "tag-neutral",
  "tag-danger",
];

const GROUP_LABELS: Record<BadgeAuditScreen["group"], string> = {
  delivery: "メール一斉配信",
  matching: "マッチング",
  crm: "CRM",
  settings: "設定",
  admin: "管理画面",
  email: "メール詳細・検索",
  home: "ホーム/プロフィール",
};

const GROUP_ORDER: BadgeAuditScreen["group"][] = [
  "delivery",
  "matching",
  "crm",
  "settings",
  "admin",
  "email",
  "home",
];

/**
 * ゼロベース再設計 + 自前トーン体系の理想形 audit view。
 * @return {JSX.Element} proposal セクション
 */
export const BadgeAuditProposalView = (): JSX.Element => {
  const screensByGroup = useMemo(() => {
    const map: Record<string, ProposalScreen[]> = {};
    for (const s of badgeAuditProposal) {
      if (!map[s.group]) map[s.group] = [];
      map[s.group].push(s);
    }
    return map;
  }, []);

  return (
    <div className="space-y-10">
      <header className="space-y-2">
        <h2 className="text-xl font-bold">
          Status / Tag / Badge — 提案 (理想形)
        </h2>
        <p className="text-sm text-base-content/70">
          DaisyUI の badge プリセットを使わず、自前定義の{" "}
          <strong>soft pastel トーン</strong> を中心に再設計した提案。 通常
          status はすべて低彩度で一覧画面のノイズを最小化し、
          致命的な失敗・重要マイルストーンだけ saturated な
          <code className="mx-1 bg-base-200 px-1 rounded">critical-*</code>
          で強調する。
        </p>
      </header>

      {/* 設計原則 */}
      <section className="space-y-3">
        <h3 className="text-base font-semibold">設計原則</h3>
        <ol className="text-sm list-decimal list-inside space-y-1 text-base-content/80">
          <li>通常の status は全て soft pastel — 一覧画面のノイズを最小化</li>
          <li>
            致命的失敗・容量超過・成約のような重大な瞬間だけ
            <code className="mx-1 bg-base-200 px-1 rounded text-xs">
              critical-*
            </code>
            で強調 (緩急をつける)
          </li>
          <li>
            「順序のある状態」(CRM ステージ等) は単一カテゴリ
            <code className="mx-1 bg-base-200 px-1 rounded text-xs">
              stage-*
            </code>
            の violet グラデーションで進行度を表現
          </li>
          <li>
            ニュートラル分類タグ (人材/案件/カテゴリ等) は
            <code className="mx-1 bg-base-200 px-1 rounded text-xs">
              tag-neutral
            </code>
            で色を持たない
          </li>
          <li>
            DaisyUI の badge-* プリセットは status に使わない — 彩度が強すぎて
            B2B SaaS の一覧と相性が悪い
          </li>
        </ol>
      </section>

      {/* トーン定義 */}
      <section className="space-y-3">
        <h3 className="text-base font-semibold">トーン定義</h3>
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          <table className="table table-xs w-full">
            <thead className="bg-primary text-white">
              <tr>
                <th>サンプル</th>
                <th>token</th>
                <th>意味</th>
              </tr>
            </thead>
            <tbody>
              {TONE_ORDER.map(tone => (
                <tr key={tone}>
                  <td>
                    <span className={badgeToneClass[tone]}>label</span>
                  </td>
                  <td>
                    <code className="text-[10px] bg-base-200 px-1 rounded">
                      {tone}
                    </code>
                  </td>
                  <td className="text-[12px]">{badgeToneDescription[tone]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* グループごとの提案 */}
      {GROUP_ORDER.map(group => {
        const screens = screensByGroup[group];
        if (!screens || screens.length === 0) return null;
        return (
          <section key={group} className="space-y-4">
            <h3 className="text-base font-semibold border-b border-slate-200 pb-1">
              {GROUP_LABELS[group]}
            </h3>
            {screens.map(s => (
              <div
                key={s.screen}
                className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
              >
                <p className="text-xs font-semibold text-base-content/70 mb-3">
                  {s.screen}
                </p>
                <div className="space-y-2">
                  {s.entries.map((e, idx) => (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: audit data is static
                      key={`${e.label}-${idx}`}
                      className="flex items-center gap-3"
                    >
                      <span className={`${badgeToneClass[e.tone]} shrink-0`}>
                        {e.label}
                      </span>
                      <code className="text-[10px] text-base-content/50 shrink-0">
                        {e.tone}
                      </code>
                      {e.reason && (
                        <span className="text-[11px] text-base-content/70">
                          ← {e.reason}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </section>
        );
      })}
    </div>
  );
};
